// Shared helpers for route-launcher card rendering (avatars, progress, timestamps).

export const AVATAR_COLORS = [
  "#2D6A4F",
  "#40916C",
  "#52B788",
  "#74C69D",
  "#1B4332",
  "#8B5E3C",
  "#A47148",
  "#C08552",
  "#3A86A1",
  "#457B9D",
  "#6D597A",
  "#B56576",
];

export function avatarInitialsFromName(name) {
  const display = (name || "").trim();
  if (!display) return "?";

  const parts = display.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  return display.slice(0, 2).toUpperCase();
}

export function avatarColorFromName(name) {
  const sum = [...String(name || "")].reduce(
    (acc, character) => acc + character.charCodeAt(0),
    0,
  );
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

export function sortRoutesByRecent(routes) {
  return [...routes].sort(
    (left, right) => new Date(right.created_at) - new Date(left.created_at),
  );
}

export function interpolateLabel(template, values) {
  return template.replace(/%\{(\w+)\}/g, (_, key) => values[key] ?? "");
}

export function formatRouteStartedAt(isoString, timeZone) {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";

  const locale = document.documentElement.lang || "es";
  const now = Date.now();
  const diffMinutes = Math.round((now - date.getTime()) / 60_000);
  const relativeFormatter = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
  });

  if (diffMinutes < 1) {
    return relativeFormatter.format(0, "minute");
  }
  if (diffMinutes < 60) {
    return relativeFormatter.format(-diffMinutes, "minute");
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return relativeFormatter.format(-diffHours, "hour");
  }

  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) {
    return relativeFormatter.format(-diffDays, "day");
  }

  return new Intl.DateTimeFormat(locale, {
    timeZone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function openEndedProgress(routeDatapoints, labels) {
  const sampleCount = routeDatapoints.length;
  const treeNumbers = routeDatapoints
    .map((datapoint) => datapoint.tree_number)
    .filter((treeNumber) => treeNumber != null && treeNumber > 0);
  const maxTree = treeNumbers.length > 0 ? Math.max(...treeNumbers) : 0;

  if (sampleCount === 0 && maxTree === 0) {
    return { progress_show: false };
  }

  const progressLabel =
    maxTree > 0
      ? interpolateLabel(labels.samplesWithTree, {
          count: sampleCount,
          tree: maxTree,
        })
      : interpolateLabel(labels.samples, { count: sampleCount });

  return {
    progress_show: true,
    progress_radial: false,
    progress_percent: 0,
    progress_label: progressLabel,
  };
}

function treeRouteProgress(route, labels) {
  const total = Number.isFinite(route.total_trees_in_route)
    ? route.total_trees_in_route
    : 0;
  const current = route.current_tree_number || 0;

  if (total <= 0) {
    return { progress_show: false };
  }

  const percent = Math.min(100, Math.round((current / total) * 100));

  return {
    progress_show: true,
    progress_radial: true,
    progress_percent: percent,
    progress_label: interpolateLabel(labels.tree, {
      current,
      total,
    }),
  };
}

export function buildInProgressRouteProgress(
  route,
  routeDatapoints,
  sanitationForm,
  labels,
) {
  const openEnded =
    sanitationForm?.open_ended === true ||
    route.total_trees_in_route === Infinity;

  if (openEnded) {
    return openEndedProgress(routeDatapoints, labels);
  }

  return treeRouteProgress(route, labels);
}

export function buildCompletedRouteProgress(
  route,
  routeDatapoints,
  sanitationForm,
  labels,
) {
  const openEnded =
    sanitationForm?.open_ended === true ||
    route.total_trees_in_route === Infinity;

  if (openEnded) {
    const openEndedProgressData = openEndedProgress(routeDatapoints, labels);
    if (!openEndedProgressData.progress_show) {
      return {
        progress_show: true,
        progress_radial: true,
        progress_percent: 100,
        progress_label: labels.completedReady,
      };
    }

    return {
      ...openEndedProgressData,
      progress_radial: true,
      progress_percent: 100,
    };
  }

  const total = Number.isFinite(route.total_trees_in_route)
    ? route.total_trees_in_route
    : route.current_tree_number || 0;

  return {
    progress_show: true,
    progress_radial: true,
    progress_percent: 100,
    progress_label: interpolateLabel(labels.tree, {
      current: total,
      total,
    }),
  };
}

export function monitorAvatarFields(monitorName) {
  return {
    monitor_initials: avatarInitialsFromName(monitorName),
    monitor_avatar_color: avatarColorFromName(monitorName),
  };
}
